import {
  type Address,
  BaseError,
  ContractFunctionRevertedError,
  ContractFunctionZeroDataError,
  type PublicClient,
} from 'viem';

import { wAlphaAbi, wsTAOAbi } from 'libs/contracts/abis/bittensorWrappers';

// Three-way wrapper-kind discriminator that mirrors PositionRouter's
// _assertWsTAOBacked / _assertWAlphaBacked, plus an 'unsupported' branch for
// underlyings that are neither (router would revert with UnderlyingMismatch).
//
// Probe order:
//   1. wsTAOColdkey() — non-zero return ⇒ wsTAO wrapper.
//   2. hotkey()       — non-zero return ⇒ wAlpha wrapper.
//   3. otherwise      ⇒ 'unsupported'.
//
// Token symbols are NOT used as fallback (mutable governance metadata).

const ZERO_BYTES32 = '0x0000000000000000000000000000000000000000000000000000000000000000';

export type VTokenWrapperKind = 'wsTAO' | 'wAlpha' | 'unsupported';

const isExpectedProbeMiss = (error: unknown) => {
  if (
    error instanceof ContractFunctionRevertedError ||
    error instanceof ContractFunctionZeroDataError
  ) {
    return true;
  }

  if (!(error instanceof BaseError)) {
    return false;
  }

  return !!error.walk(
    (cause) =>
      cause instanceof ContractFunctionRevertedError ||
      cause instanceof ContractFunctionZeroDataError,
  );
};

export const getVTokenWrapperKind = async ({
  publicClient,
  underlyingAddress,
}: {
  publicClient: PublicClient;
  underlyingAddress: Address;
}): Promise<VTokenWrapperKind> => {
  try {
    const coldkey = await publicClient.readContract({
      address: underlyingAddress,
      abi: wsTAOAbi,
      functionName: 'wsTAOColdkey',
    });
    if (coldkey && coldkey !== ZERO_BYTES32) {
      return 'wsTAO';
    }
  } catch (error) {
    if (!isExpectedProbeMiss(error)) {
      throw error;
    }
    // wsTAOColdkey() not present on this contract → fall through to wAlpha probe.
  }
  try {
    const hk = await publicClient.readContract({
      address: underlyingAddress,
      abi: wAlphaAbi,
      functionName: 'hotkey',
    });
    if (hk && hk !== ZERO_BYTES32) {
      return 'wAlpha';
    }
  } catch (error) {
    if (!isExpectedProbeMiss(error)) {
      throw error;
    }
    // hotkey() not present on this contract → not a wAlpha wrapper either.
  }
  return 'unsupported';
};
